<template>
  <v-container fluid class="pa-5">
    <v-card class="mb-4">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-school</v-icon>
        สถิตินักเรียนอาชีวศึกษารายจังหวัด (ข้อมูลดิบ ตั้งแต่ปีแรกเข้า 2558)
        <v-spacer></v-spacer>
      <v-btn text small color="white" @click="guideDialog = true">
        <v-icon left small>mdi-help-circle-outline</v-icon>วิธีใช้งาน
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row dense>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="selectedProvince"
            :items="provinceOptions"
            label="ค้นหาจังหวัด"
            prepend-icon="mdi-map-marker"
            clearable
            dense
            outlined
          ></v-autocomplete>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="selectedRegion"
            :items="regionOptions"
            label="กรองรายชื่อจังหวัดตามภาค"
            prepend-icon="mdi-map"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="selectedStudentType"
            :items="studentTypeOptions"
            label="ประเภทนักเรียน"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="ปีแรกเข้า (ปีการศึกษา)"
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="6" sm="3" md="1">
          <v-btn color="primary" block large @click="loadProvinceData" :loading="loadingDetail" :disabled="!selectedProvince">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" md="2" class="text-right">
          <v-btn color="success" outlined @click="exportExcel" :disabled="!selectedProvince || !provinceTrend.length">
            <v-icon left>mdi-file-excel</v-icon>XLSX
          </v-btn>
        </v-col>
      </v-row>

      <div v-if="!selectedProvince && !loading" class="text-center grey--text py-8">
        <v-icon size="64" color="grey lighten-1">mdi-map-search-outline</v-icon>
        <div class="mt-2">เลือกจังหวัดเพื่อดูสถิตินักเรียนแรกเข้า ย้อนหลังตั้งแต่ปีการศึกษา 2558</div>
        <div class="caption mt-1">
          ข้อมูลชุดนี้ครอบคลุม {{ provinceOptions.length }} จังหวัด ในภาคกลาง ภาคตะวันออก/กรุงเทพฯ-ปริมณฑล
          และบางส่วนของภาคตะวันออกเฉียงเหนือ (ตามที่มีอยู่จริงในตาราง studentlist) —
          จังหวัดในภาคเหนือและภาคใต้ยังไม่มีข้อมูลในชุดนี้
        </div>
      </div>

      <template v-if="selectedProvince && provinceTrend.length">
        <v-alert v-if="selectedYearHasNoData" type="warning" text dense border="left" class="mt-2">
          ยังไม่มีข้อมูลนักเรียนแรกเข้าปีการศึกษา {{ selectedYear }} ในจังหวัด{{ selectedProvince }}
          <span v-if="latestYearWithData">— ข้อมูลล่าสุดที่มีขณะนี้คือปีการศึกษา {{ latestYearWithData }}</span>
        </v-alert>
        <v-alert v-else-if="isLatestYearSelected" type="info" text dense border="left" class="mt-2">
          ข้อมูลปีการศึกษา {{ selectedYear }} อาจยังไม่สมบูรณ์ เนื่องจากอยู่ระหว่างการนำเข้าข้อมูลนักเรียนแรกเข้าของปีนี้
        </v-alert>

        <!-- การ์ดสถิติสรุป -->
        <v-row v-if="!selectedYearHasNoData" dense class="mt-2">
          <v-col cols="6" sm="4" md="2">
            <v-card color="info" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">{{ formatNum(currentYearData.total) }}</div>
              <div style="font-size:12px;">นักเรียนแรกเข้า ({{ selectedYear }})</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card :color="yoyChange >= 0 ? 'success' : 'error'" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">
                <v-icon dark small>{{ yoyChange >= 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}</v-icon>
                {{ Math.abs(yoyChange).toFixed(1) }}%
              </div>
              <div style="font-size:12px;">เทียบปีก่อนหน้า</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card color="info" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">{{ currentYearData.college_count }}</div>
              <div style="font-size:12px;">สถานศึกษา</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card color="primary" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">{{ currentYearData.percentage_of_region.toFixed(1) }}%</div>
              <div style="font-size:12px;">สัดส่วนของภาค</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card color="success darken-1" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">{{ retentionRate.toFixed(1) }}%</div>
              <div style="font-size:12px;">อัตราคงอยู่/สำเร็จการศึกษา</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card color="primary" dark class="pa-3 text-center d-flex flex-column justify-center" height="95px">
              <div class="text-h5 font-weight-bold">{{ attritionRate.toFixed(1) }}%</div>
              <div style="font-size:12px;">อัตราออกกลางคัน</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- ข้อความสรุปเชิงวิเคราะห์ -->
        <v-alert v-if="!selectedYearHasNoData" :type="yoyChange >= 0 ? 'success' : 'warning'" text dense border="left" class="mt-4">
          {{ insightText }}
        </v-alert>

        <v-row class="mt-2">
          <!-- กราฟแนวโน้ม -->
          <v-col cols="12" md="7">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                แนวโน้มจำนวนนักเรียนแรกเข้า แยกตามระดับ ปวช./ปวส. ({{ trendYears[0] }} - {{ trendYears[trendYears.length - 1] }})
              </v-card-subtitle>
              <v-card-text>
                <apexchart type="line" height="280" :options="trendChart.opts" :series="trendChart.series"></apexchart>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- สัดส่วนตามประเภทการศึกษา -->
          <v-col cols="12" md="5">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">สัดส่วนตามประเภทการศึกษา ({{ selectedYear }})</v-card-subtitle>
              <v-card-text>
                <apexchart
                  v-if="classChart.series.length"
                  type="donut"
                  height="280"
                  :options="classChart.opts"
                  :series="classChart.series"
                ></apexchart>
                <div v-else class="text-center grey--text py-8">ไม่มีข้อมูล</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- ชั้นปีและอัตราออกกลางคัน -->
        <v-row class="mt-2">
          <v-col cols="12">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                จำนวนนักเรียนตามชั้นปี และอัตราออกกลางคันรายชั้นปี ({{ selectedYear }})
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="7">
                    <apexchart
                      v-if="gradeChart.series.length"
                      type="bar"
                      height="260"
                      :options="gradeChart.opts"
                      :series="gradeChart.series"
                    ></apexchart>
                    <div v-else class="text-center grey--text py-8">ไม่มีข้อมูล</div>
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>ชั้นปี</th>
                          <th class="text-right">นักเรียนแรกเข้า</th>
                          <th class="text-right">อัตราออกกลางคัน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in gradeBreakdown" :key="row.grade_name">
                          <td>{{ row.grade_name }}</td>
                          <td class="text-right">{{ formatNum(row.total_entrants) }}</td>
                          <td class="text-right">
                            <v-chip x-small dark :color="gradeAttritionColor(row)">
                              {{ gradeAttritionRate(row).toFixed(1) }}%
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <!-- ตารางวิทยาลัยในจังหวัด -->
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                สถานศึกษาในจังหวัด เรียงตามจำนวนนักเรียนแรกเข้า ({{ selectedYear }})
              </v-card-subtitle>
              <v-card-text class="pb-0">
                <v-text-field v-model="collegeSearch" label="ค้นหาสถานศึกษา" dense outlined hide-details prepend-inner-icon="mdi-magnify"></v-text-field>
              </v-card-text>
              <v-data-table
                :headers="collegeHeaders"
                :items="collegeList"
                :search="collegeSearch"
                :loading="loadingDetail"
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 25, -1] }"
              >
                <template v-slot:item.total_entrants="{ item }">
                  <strong>{{ formatNum(item.total_entrants) }}</strong>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <!-- ตารางสาขายอดนิยม -->
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                สาขาวิชายอดนิยมในจังหวัด ({{ selectedYear }})
              </v-card-subtitle>
              <v-card-text class="pb-0">
                <v-text-field v-model="branchSearch" label="ค้นหาสาขาวิชา" dense outlined hide-details prepend-inner-icon="mdi-magnify"></v-text-field>
              </v-card-text>
              <v-data-table
                :headers="branchHeaders"
                :items="branchList"
                :search="branchSearch"
                :loading="loadingDetail"
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 25, -1] }"
              >
                <template v-slot:item.total_entrants="{ item }">
                  <strong>{{ formatNum(item.total_entrants) }}</strong>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <div v-else-if="selectedProvince && !loading" class="text-center grey--text py-8">
        ไม่พบข้อมูลนักเรียนของจังหวัดนี้
      </div>
    </v-card-text>

    <v-dialog v-model="guideDialog" max-width="600">
      <v-card rounded="lg">
        <div class="guide-header">
          <v-icon size="32" color="white" class="mr-3">mdi-school-outline</v-icon>
          <div>
            <h3 class="white--text mb-0">วิธีใช้งานสถิตินักเรียนรายจังหวัด</h3>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="guideDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="pt-5">
          <p>
            ข้อมูลหน้านี้มาจากตาราง <strong>studentlist</strong> (ข้อมูลดิบระดับรายบุคคล) นับตาม
            "ปีแรกเข้า" ของนักเรียนแต่ละคน จึงรองรับย้อนหลังได้ตั้งแต่ปีการศึกษา 2558 เป็นต้นมา
          </p>
          <p>
            เลือกจังหวัด (กรองรายชื่อจังหวัดตามภาคได้ก่อน), ประเภทนักเรียน และปีที่ต้องการ แล้วกด
            "ค้นหา" (ไอคอนแว่นขยาย) เพื่อดู:
          </p>
          <ul>
            <li><strong>การ์ดสถิติ</strong> — จำนวนนักเรียนแรกเข้า, การเปลี่ยนแปลงเทียบปีก่อนหน้า, จำนวนสถานศึกษา, สัดส่วนเทียบภาค, อัตราคงอยู่/สำเร็จการศึกษา, อัตราออกกลางคัน</li>
            <li><strong>กราฟแนวโน้ม</strong> — จำนวนนักเรียนแรกเข้าย้อนหลังตั้งแต่ปีการศึกษา 2558 ถึงปีที่เลือก แยกเป็น 3 เส้น: รวมทุกระดับ, ปวช., และ ปวส. เพื่อเปรียบเทียบแนวโน้มของแต่ละระดับแยกกัน</li>
            <li><strong>สัดส่วนตามประเภทการศึกษา</strong> — ปวช./ปวส. ของนักเรียนแรกเข้าปีนั้นในจังหวัดนี้</li>
            <li>
              <strong>ชั้นปีและอัตราออกกลางคัน</strong> — แยกละเอียดถึงระดับชั้นปี (ปวช.1/2/3, ปวส.1/2)
              พร้อมอัตราออกกลางคันต่อชั้นปี ช่วยดูว่าการออกกลางคันกระจุกตัวที่ชั้นปีใด (มักพบมากสุด
              ในปีแรกของแต่ละหลักสูตร)
            </li>
            <li><strong>ตารางสถานศึกษา / สาขายอดนิยม</strong> — ค้นหาในตารางได้โดยตรง เรียงตามจำนวนนักเรียนแรกเข้ามากไปน้อย</li>
          </ul>
          <p>
            <strong>ตัวกรอง "ประเภทนักเรียน"</strong> (ปกติ/ทวิภาคี/ภาคสมทบ/ผู้พิการ/เกษตรปฏิรูป/อศ.กช)
            ใช้ร่วมกับจังหวัดและปีได้ เพื่อดูเฉพาะกลุ่มนักเรียนที่ต้องการ
          </p>
          <p class="mb-0">
            กดปุ่ม <strong>"XLSX"</strong> เพื่อดาวน์โหลดข้อมูลแนวโน้มทั้งหมดของจังหวัดที่เลือกเป็นไฟล์ Excel — ข้อมูล
            ชุดนี้ครอบคลุมเฉพาะจังหวัดในภาคกลาง ภาคตะวันออก/กรุงเทพฯ-ปริมณฑล และบางส่วนของภาคตะวันออกเฉียงเหนือ
            (ตามที่มีจริงในตาราง studentlist) ยังไม่มีข้อมูลภาคเหนือและภาคใต้
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" rounded @click="guideDialog = false">เข้าใจแล้ว</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
  </v-container>
</template>

<script>
export default {
  name: "StudentProvinceDashboard",
  data: () => ({
    ApiKey: "HRvec2021",
    loading: false,
    loadingDetail: false,
    guideDialog: false,
    selectedProvince: null,
    selectedRegion: null,
    selectedStudentType: null,
    selectedYear: "2568",
    minYear: "2558",
    // 2569/2570 ยังไม่มีข้อมูลจริงใน studentlist ณ ตอนนี้ (ปีล่าสุดที่มีข้อมูลคือ 2568) แต่ให้เลือก
    // ได้ล่วงหน้าตามรูปแบบเดียวกับตัวเลือกปีในหน้าอื่นของระบบ (เช่น year_ss ใน
    // conditons_transfer_success.vue ที่มีถึง 2570) — ถ้าเลือกปีที่ยังไม่มีข้อมูล จะขึ้นข้อความ
    // "ไม่พบข้อมูล" ตามปกติ ไม่ใช่ error
    yearOptions: ["2558", "2559", "2560", "2561", "2562", "2563", "2564", "2565", "2566", "2567", "2568", "2569", "2570"],
    studentTypeOptions: ["ปกติ", "ทวิภาคี", "ภาคสมทบ", "ผู้พิการ", "เกษตรปฏิรูป", "อศ.กช"],
    allProvinceData: [], // ทุกจังหวัด ทุกปี (ตั้งแต่ minYear) — โหลดใหม่ทุกครั้งที่เปลี่ยนตัวกรองประเภทนักเรียน ใช้ทำ dropdown + กราฟแนวโน้ม
    collegeList: [],
    branchList: [],
    classBreakdown: [],
    gradeBreakdown: [],
    classTrendData: [], // แนวโน้มแยกเส้น ปวช./ปวส. ทุกปี ของจังหวัดที่เลือกอยู่ — เสริมกราฟแนวโน้มหลัก
    collegeSearch: "",
    branchSearch: "",
    collegeHeaders: [
      { text: "สถานศึกษา", value: "college_name" },
      { text: "นักเรียนแรกเข้า", value: "total_entrants", align: "end" }
    ],
    branchHeaders: [
      { text: "สาขาวิชา", value: "branch_name" },
      { text: "นักเรียนแรกเข้า", value: "total_entrants", align: "end" }
    ]
  }),

  async mounted() {
    await this.loadAllProvinceData();
  },

  computed: {
    regionOptions() {
      // สร้างจากข้อมูลที่โหลดมาจริงเท่านั้น (ไม่ใช่รายชื่อภาคมาตรฐานทั้งหมด) เพื่อไม่ให้เลือกภาคที่
      // ไม่มีข้อมูลในตาราง studentlist เลย (เช่น ภาคเหนือ/ภาคใต้) แล้วเจอรายชื่อจังหวัดว่างเปล่า
      const set = new Set(this.allProvinceData.map(r => r.region_name).filter(Boolean));
      return Array.from(set).sort();
    },
    provinceOptions() {
      // กรองตามภาคที่เลือกไว้ก่อน (ถ้ามี) — ใช้ narrow รายชื่อจังหวัดในช่องค้นหาให้เลือกง่ายขึ้น
      const rows = this.selectedRegion
        ? this.allProvinceData.filter(r => r.region_name === this.selectedRegion)
        : this.allProvinceData;
      const set = new Set(rows.map(r => r.province_name).filter(Boolean));
      return Array.from(set).sort();
    },
    provinceTrend() {
      if (!this.selectedProvince) return [];
      return this.allProvinceData
        .filter(r => r.province_name === this.selectedProvince)
        .sort((a, b) => a.entranceYear - b.entranceYear);
    },
    trendYears() {
      return this.provinceTrend.map(r => r.entranceYear);
    },
    // ปีล่าสุดที่ "มีข้อมูลจริง" ในจังหวัดที่กำลังดูอยู่ — คำนวณจากข้อมูลที่โหลดมาจริง ไม่ใช่ปีสุดท้าย
    // ใน yearOptions ตรงๆ เพราะ yearOptions เผื่อปีอนาคต (2569/2570) ไว้ล่วงหน้าตามธรรมเนียมของ
    // ระบบ แม้จะยังไม่มีข้อมูลจริงก็ตาม
    latestYearWithData() {
      if (!this.trendYears.length) return null;
      return Math.max(...this.trendYears.map(y => Number(y)));
    },
    // true เมื่อเลือกปีที่ "มีข้อมูล" แต่เป็นปีล่าสุด (อาจยังนำเข้าไม่ครบ) — ต่างจาก
    // selectedYearHasNoData ที่หมายถึงยังไม่มีข้อมูลเลยสำหรับปีนั้น (เช่นปีอนาคตที่ยังไม่เริ่มภาคเรียน)
    isLatestYearSelected() {
      return this.latestYearWithData !== null && String(this.selectedYear) === String(this.latestYearWithData);
    },
    selectedYearHasNoData() {
      return !this.provinceTrend.some(r => String(r.entranceYear) === String(this.selectedYear));
    },
    currentYearData() {
      const row = this.provinceTrend.find(r => String(r.entranceYear) === String(this.selectedYear)) || {};
      const total = Number(row.total_entrants) || 0;
      // ร้อยละของภาค: คำนวณเองฝั่ง frontend จากข้อมูลทุกจังหวัดที่โหลดมาแล้ว (ไม่ต้องยิง request เพิ่ม)
      const regionTotal = this.allProvinceData
        .filter(r => r.region_name === row.region_name && String(r.entranceYear) === String(this.selectedYear))
        .reduce((s, r) => s + (Number(r.total_entrants) || 0), 0);
      return {
        total,
        college_count: row.college_count || 0,
        region: row.region_name || "",
        percentage_of_region: regionTotal ? (total / regionTotal) * 100 : 0,
        studying: Number(row.studying) || 0,
        graduated: Number(row.graduated) || 0,
        discontinued: Number(row.discontinued) || 0,
        withdrawn: Number(row.withdrawn) || 0
      };
    },
    retentionRate() {
      const d = this.currentYearData;
      if (!d.total) return 0;
      return ((d.studying + d.graduated) / d.total) * 100;
    },
    attritionRate() {
      const d = this.currentYearData;
      if (!d.total) return 0;
      return ((d.discontinued + d.withdrawn) / d.total) * 100;
    },
    yoyChange() {
      const idx = this.provinceTrend.findIndex(r => String(r.entranceYear) === String(this.selectedYear));
      if (idx <= 0) return 0;
      const prev = Number(this.provinceTrend[idx - 1].total_entrants) || 0;
      const curr = this.currentYearData.total;
      if (!prev) return 0;
      return ((curr - prev) / prev) * 100;
    },
    insightText() {
      if (!this.provinceTrend.length) return "";
      const dir = this.yoyChange >= 0 ? "เพิ่มขึ้น" : "ลดลง";
      const pctText = Math.abs(this.yoyChange).toFixed(1);
      return `จำนวนนักเรียนแรกเข้าในจังหวัด${this.selectedProvince} ปีการศึกษา ${this.selectedYear} มีทั้งหมด ${this.formatNum(
        this.currentYearData.total
      )} คน ${dir} ${pctText}% เมื่อเทียบกับปีก่อนหน้า คิดเป็น ${this.currentYearData.percentage_of_region.toFixed(
        1
      )}% ของนักเรียนแรกเข้าใน${this.currentYearData.region} — อัตราคงอยู่/สำเร็จการศึกษา ${this.retentionRate.toFixed(1)}%`;
    },
    // เส้น ปวช./ปวส. แยกตามปี จับคู่กับปีเดียวกับเส้น "รวม" (this.trendYears) เสมอ — ปีไหนไม่มี
    // ข้อมูลของประเภทนั้นให้เป็น 0 แทนที่จะขาดหายไปจากกราฟ เพื่อให้เส้นทั้งสามจับคู่แกน X กันได้ตรง
    classTrendSeries() {
      const byClassYear = {};
      this.classTrendData.forEach(r => {
        byClassYear[`${r.college_class}|${r.entranceYear}`] = Number(r.total_entrants) || 0;
      });
      const buildSeries = className =>
        this.trendYears.map(y => byClassYear[`${className}|${y}`] || 0);
      return {
        ปวช: buildSeries("ปวช."),
        ปวส: buildSeries("ปวส.")
      };
    },
    trendChart() {
      return {
        series: [
          { name: "รวมทุกระดับ", data: this.provinceTrend.map(r => Number(r.total_entrants) || 0) },
          { name: "ปวช.", data: this.classTrendSeries.ปวช },
          { name: "ปวส.", data: this.classTrendSeries.ปวส }
        ],
        opts: {
          chart: { type: "line", toolbar: { show: false }, fontFamily: "inherit" },
          colors: ["#B3151B", "#424242", "#E57373"],
          stroke: { width: [3, 2, 2], curve: "smooth" },
          markers: { size: 4 },
          xaxis: {
            categories: this.trendYears,
            labels: { style: { fontSize: "12px", colors: "#475569" } }
          },
          yaxis: { labels: { formatter: v => this.formatNum(v) } },
          grid: { borderColor: "#e4e4e7" },
          dataLabels: { enabled: false },
          legend: { position: "top" },
          tooltip: { y: { formatter: v => this.formatNum(v) + " คน" } }
        }
      };
    },
    classChart() {
      if (!this.classBreakdown.length) return { series: [], opts: {} };
      return {
        series: this.classBreakdown.map(r => Number(r.total_entrants) || 0),
        opts: {
          chart: { type: "donut", fontFamily: "inherit" },
          labels: this.classBreakdown.map(r => r.college_class),
          colors: ["#B3151B", "#424242", "#E57373"],
          legend: { position: "bottom" },
          dataLabels: { formatter: v => v.toFixed(1) + "%" },
          tooltip: { y: { formatter: v => this.formatNum(v) + " คน" } }
        }
      };
    },
    gradeChart() {
      if (!this.gradeBreakdown.length) return { series: [], opts: {} };
      return {
        series: [{ name: "นักเรียนแรกเข้า", data: this.gradeBreakdown.map(r => Number(r.total_entrants) || 0) }],
        opts: {
          chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
          colors: ["#B3151B"],
          plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
          xaxis: {
            categories: this.gradeBreakdown.map(r => r.grade_name),
            labels: { style: { fontSize: "11px", colors: "#475569" } }
          },
          yaxis: { labels: { formatter: v => this.formatNum(v) } },
          grid: { borderColor: "#e4e4e7" },
          dataLabels: { enabled: true, formatter: v => this.formatNum(v) },
          tooltip: { y: { formatter: v => this.formatNum(v) + " คน" } }
        }
      };
    }
  },

  methods: {
    formatNum(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },

    // อัตราออกกลางคันต่อชั้นปี = (พ้นสภาพ + ลาออก) / นักเรียนแรกเข้าของชั้นปีนั้น — ใช้เกณฑ์เดียวกับ
    // attritionRate ระดับจังหวัด แต่แยกย่อยลงไปถึงระดับชั้นปีจริง (ปวช.1/2/3, ปวส.1/2)
    gradeAttritionRate(row) {
      const total = Number(row.total_entrants) || 0;
      if (!total) return 0;
      const attrited = (Number(row.discontinued) || 0) + (Number(row.withdrawn) || 0);
      return (attrited / total) * 100;
    },
    gradeAttritionColor(row) {
      const rate = this.gradeAttritionRate(row);
      if (rate >= 30) return "error";
      if (rate >= 15) return "warning";
      return "success";
    },

    async loadAllProvinceData() {
      this.loading = true;
      try {
        const result = await this.$http.post("student_entrance_stats.read.php", {
          ApiKey: this.ApiKey,
          group_by: "province",
          min_year: this.minYear,
          student_type_name: this.selectedStudentType || undefined
        });
        this.allProvinceData = result.data && result.data.status ? result.data.data : [];
        // ถ้าจังหวัด/ภาคที่เลือกไว้ไม่มีในผลลัพธ์ใหม่ (เช่น เปลี่ยนตัวกรองประเภทนักเรียนแล้วไม่มีข้อมูล
        // เหลือ) ให้ล้างค่าที่เลือกไว้ ป้องกันหน้าจอค้างแสดงข้อมูลเก่าที่ไม่ตรงกับตัวกรองปัจจุบัน
        if (this.selectedProvince && !this.provinceOptions.includes(this.selectedProvince)) {
          this.selectedProvince = null;
        }
      } catch (error) {
        console.error("Error loading province data:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadProvinceData() {
      if (!this.selectedProvince) return;
      this.loadingDetail = true;
      try {
        const [collegeRes, branchRes, classRes, gradeRes, classTrendRes] = await Promise.all([
          this.$http.post("student_entrance_stats.read.php", {
            ApiKey: this.ApiKey,
            group_by: "college",
            province_name: this.selectedProvince,
            year: this.selectedYear,
            student_type_name: this.selectedStudentType || undefined
          }),
          this.$http.post("student_entrance_stats.read.php", {
            ApiKey: this.ApiKey,
            group_by: "branch",
            province_name: this.selectedProvince,
            year: this.selectedYear,
            student_type_name: this.selectedStudentType || undefined
          }),
          this.$http.post("student_entrance_stats.read.php", {
            ApiKey: this.ApiKey,
            group_by: "class",
            province_name: this.selectedProvince,
            year: this.selectedYear,
            student_type_name: this.selectedStudentType || undefined
          }),
          this.$http.post("student_entrance_stats.read.php", {
            ApiKey: this.ApiKey,
            group_by: "grade",
            province_name: this.selectedProvince,
            year: this.selectedYear,
            student_type_name: this.selectedStudentType || undefined
          }),
          this.$http.post("student_entrance_stats.read.php", {
            ApiKey: this.ApiKey,
            group_by: "class_trend",
            province_name: this.selectedProvince,
            min_year: this.minYear,
            student_type_name: this.selectedStudentType || undefined
          })
        ]);

        this.collegeList = collegeRes.data && collegeRes.data.status ? collegeRes.data.data : [];
        this.branchList = branchRes.data && branchRes.data.status ? branchRes.data.data : [];
        this.classBreakdown = classRes.data && classRes.data.status ? classRes.data.data : [];
        this.gradeBreakdown = gradeRes.data && gradeRes.data.status ? gradeRes.data.data : [];
        this.classTrendData = classTrendRes.data && classTrendRes.data.status ? classTrendRes.data.data : [];
      } catch (error) {
        console.error("Error loading province detail:", error);
      } finally {
        this.loadingDetail = false;
      }
    },

    async exportExcel() {
      if (!this.provinceTrend.length) return;
      try {
        const trendSheet = this.provinceTrend.map((r, i) => ({
          "จังหวัด": r.province_name,
          "ภาค": r.region_name,
          "ปีแรกเข้า": r.entranceYear,
          "นักเรียนแรกเข้า (รวม)": Number(r.total_entrants) || 0,
          "ปวช.": this.classTrendSeries.ปวช[i] || 0,
          "ปวส.": this.classTrendSeries.ปวส[i] || 0,
          "จำนวนสถานศึกษา": r.college_count,
          "กำลังศึกษา": Number(r.studying) || 0,
          "สำเร็จการศึกษา": Number(r.graduated) || 0,
          "พ้นสภาพ": Number(r.discontinued) || 0,
          "ลาออก": Number(r.withdrawn) || 0
        }));

        const collegeSheet = this.collegeList.map(r => ({
          "สถานศึกษา": r.college_name,
          "นักเรียนแรกเข้า": Number(r.total_entrants) || 0,
          "กำลังศึกษา": Number(r.studying) || 0,
          "สำเร็จการศึกษา": Number(r.graduated) || 0,
          "พ้นสภาพ": Number(r.discontinued) || 0,
          "ลาออก": Number(r.withdrawn) || 0
        }));

        const branchSheet = this.branchList.map(r => ({
          "สาขาวิชา": r.branch_name,
          "นักเรียนแรกเข้า": Number(r.total_entrants) || 0
        }));

        const gradeSheet = this.gradeBreakdown.map(r => ({
          "ชั้นปี": r.grade_name,
          "ประเภทการศึกษา": r.college_class,
          "นักเรียนแรกเข้า": Number(r.total_entrants) || 0,
          "กำลังศึกษา": Number(r.studying) || 0,
          "สำเร็จการศึกษา": Number(r.graduated) || 0,
          "พ้นสภาพ": Number(r.discontinued) || 0,
          "ลาออก": Number(r.withdrawn) || 0,
          "อัตราออกกลางคัน (%)": Number(this.gradeAttritionRate(r).toFixed(1))
        }));

        const XLSX = await import("xlsx");
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(trendSheet), "แนวโน้มรายปี");
        if (gradeSheet.length) {
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(gradeSheet), `ชั้นปี ${this.selectedYear}`);
        }
        if (collegeSheet.length) {
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(collegeSheet), `สถานศึกษา ${this.selectedYear}`);
        }
        if (branchSheet.length) {
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(branchSheet), `สาขาวิชา ${this.selectedYear}`);
        }
        XLSX.writeFile(workbook, `สถิตินักเรียนแรกเข้า_${this.selectedProvince}_${Date.now()}.xlsx`);
      } catch (error) {
        console.error("Export Excel Error:", error);
      }
    }
  },

  watch: {
    selectedProvince(val) {
      if (val) this.loadProvinceData();
    },
    selectedYear() {
      if (this.selectedProvince) this.loadProvinceData();
    },
    selectedRegion() {
      // ถ้าจังหวัดที่เลือกไว้ไม่อยู่ในภาคที่กรองใหม่ ให้ล้างค่าจังหวัดทิ้ง ป้องกันสถานะไม่สอดคล้องกัน
      if (this.selectedProvince && !this.provinceOptions.includes(this.selectedProvince)) {
        this.selectedProvince = null;
      }
    },
    selectedStudentType() {
      // ตัวกรองนี้กระทบทั้งกราฟแนวโน้ม (ต้องโหลดใหม่ทั้งชุด) และรายละเอียดของจังหวัดที่เลือกอยู่
      this.loadAllProvinceData().then(() => {
        if (this.selectedProvince) this.loadProvinceData();
      });
    }
  }
};
</script>

<style scoped>
.guide-header {
  background: linear-gradient(135deg, #455a64 0%, #455a64 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
}
</style>
